import { Box, Button, Card, Checkbox, Flex, Heading, IconButton, Table, Text } from '@chakra-ui/react';
import { Service, TemporaryAvailabilityLimitationItemFragment } from '@client/graphql/types/graphql.ts';
import AvailabilityTemporaryCreate from '@client/page/management/availability-limitation/component/availability-temporary-create.tsx';
import AvailabilityTemporaryUpdate from '@client/page/management/availability-limitation/component/availability-temporary-update.tsx';
import { ArrowPathIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context.ts';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import { useDialog } from '@packages/ui/modal/use-dialog';
import { useNotificationCenter } from '@packages/ui/notification/notification-center';
import { useTranslate } from '@tolgee/react';
import moment from 'moment-timezone';
import { useState } from 'react';
import useDeleteTemporary from '../logic/use-delete-temporary';

export default function AvailabilityLimitationTemporaryList(props: {
  service: Service;
  data: TemporaryAvailabilityLimitationItemFragment[];
  refetch: () => void;
}) {
  const { t } = useTranslate();
  const timezone = useFocusedCompanyContext().settings.timezone;
  const dialogManager = useModalManagerContext();
  const [deleteTemporary, { loading: deleting }] = useDeleteTemporary();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const [selection, setSelection] = useState<string[]>([]);

  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < props.data.length;

  const createModal = () => {
    dialogManager.showDialog((onClose) => <AvailabilityTemporaryCreate service={props.service} onClose={onClose} />);
  };

  const updateModal = (data: TemporaryAvailabilityLimitationItemFragment) => {
    dialogManager.showDialog((onClose) => (
      <AvailabilityTemporaryUpdate service={props.service} data={data} onClose={onClose} />
    ));
  };

  const handleBatchDelete = () => {
    dialog.destructAlert({
      title: t('common.action.delete'),
      message: t('alert.delete.confirm', 'Are you sure you want to delete the selected items?'),
      textButton: t('common.button.delete'),
      onConfirm: async () => {
        try {
          await Promise.all(selection.map((item) => deleteTemporary({ variables: { id: item } })));
          showAlert({
            status: 'success',
            title: t('common.action.delete'),
            message: t('alert.delete.success', 'Deleted successfully!'),
          });
          setSelection([]);
          props.refetch();
        } catch {
          showAlert({
            status: 'error',
            title: t('common.action.delete'),
            message: t('alert.delete.failed', 'Delete failed!'),
          });
        }
      },
    });
  };

  const sortedDataByStartTime = props.data.slice().sort((a, b) => {
    const timeA = a.startTime;
    const timeB = b.startTime;
    return timeA.localeCompare(timeB);
  });

  const rows = sortedDataByStartTime.map((temporary) => {
    const { startHour, endHour, startDate, endDate } = resolveTemporaryTime(
      temporary.startTime,
      temporary.endTime,
      timezone,
    );
    return (
      <Table.Row
        borderBottom={'1px solid'}
        borderBottomColor={'gray.100'}
        key={temporary.id}
        cursor="pointer"
        _hover={{ bg: 'gray.50' }}
      >
        <Table.Cell>
          <Checkbox.Root
            size="sm"
            variant={'subtle'}
            top="0.5"
            aria-label="Select row"
            checked={selection.includes(temporary.id)}
            onCheckedChange={(changes) => {
              setSelection((prev) =>
                changes.checked ? [...prev, temporary.id] : selection.filter((name) => name !== temporary.id),
              );
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        </Table.Cell>
        <Table.Cell
          onClick={() => {
            updateModal(temporary);
          }}
          py={4}
          textAlign="left"
        >
          <Flex flexWrap="wrap" gap={2}>
            {startDate === endDate ? (
              <Text fontWeight="normal" letterSpacing="wide">
                {endDate}
              </Text>
            ) : (
              <Flex direction={{ base: 'column', lg: 'row' }} flexWrap="wrap" gap={2} align="center">
                <Text fontWeight="normal" letterSpacing="wide">
                  {startDate}
                </Text>
                <ArrowRightIcon className="w-3 h-3 hidden lg:inline-block" />
                <Text fontWeight="normal" letterSpacing="wide">
                  {endDate}
                </Text>
              </Flex>
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell
          onClick={() => {
            updateModal(temporary);
          }}
          textAlign="center"
        >
          <Flex direction={{ base: 'column', lg: 'row' }} align="center" justify="center" gap={2} flexWrap="wrap">
            <Text>{startHour}</Text>
            <ArrowRightIcon className="w-3 h-3 hidden lg:inline-block" />
            <Text>{endHour}</Text>
          </Flex>
        </Table.Cell>
        <Table.Cell
          onClick={() => {
            updateModal(temporary);
          }}
          textAlign="center"
          display={{ base: 'none', md: 'table-cell' }}
        >
          <Box
            fontWeight="normal"
            letterSpacing="wide"
            maxW="240px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            color="gray.600"
            mx="auto"
          >
            {temporary.message}
          </Box>
        </Table.Cell>
        <Table.Cell
          onClick={() => {
            updateModal(temporary);
          }}
          textAlign="right"
        >
          <Text fontWeight="normal" letterSpacing="wide">
            {temporary.capacity}
          </Text>
        </Table.Cell>
      </Table.Row>
    );
  });

  return (
    <>
      <Card.Root>
        <Card.Header py={4}>
          <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
            <Heading size="3xl" fontWeight={'bold'}>
              <Text fontSize={'2xl'}> {t('availability-limitation-temporary-list.title')}</Text>
            </Heading>
            <Flex gap={2}>
              <Button onClick={createModal} bgColor="gray.800" color={'white'} p={2}>
                {t('common.button.create')}
              </Button>
              {selection.length > 0 && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBatchDelete();
                  }}
                  bgColor="red.500"
                  color={'white'}
                  p={2}
                  border={'1px solid'}
                  borderColor={'gray.200'}
                  disabled={deleting}
                >
                  {t('common.button.delete-batch', 'Delete batch')}
                </Button>
              )}
              <IconButton onClick={props.refetch} variant="outline" aria-label="Refresh">
                <ArrowPathIcon style={{ width: '20px', height: '20px' }} />
              </IconButton>
            </Flex>
          </Flex>
        </Card.Header>
        <Card.Body p={0}>
          <Table.Root size="md" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="6">
                  <Checkbox.Root
                    size="sm"
                    top="0.5"
                    aria-label="Select all rows"
                    checked={indeterminate ? 'indeterminate' : selection.length > 0}
                    onCheckedChange={(changes) => {
                      setSelection(changes.checked ? props.data.map((item) => item.id) : []);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="left">
                  {t('availability-limitation-temporary-list.table-header-column.period')}
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="center">
                  {t('availability-limitation-temporary-list.table-header-column.during')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="center"
                  fontWeight={'semibold'}
                  display={{ base: 'none', md: 'table-cell' }}
                >
                  {t('availability-limitation-temporary-list.table-header-column.note')}
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="right">
                  {t('availability-limitation-temporary-list.table-header-column.capacity')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>{rows}</Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>
    </>
  );
}

function resolveTemporaryTime(startTimeParam: string, endTimeParam: string, timezone: string) {
  const startTime = moment.tz(startTimeParam, timezone);
  const endTime = moment.tz(endTimeParam, timezone);

  return {
    startHour: startTime.format('HH:mm'),
    endHour: endTime.format('HH:mm'),
    startDate: startTime.format('YYYY-MM-DD'),
    endDate: endTime.format('YYYY-MM-DD'),
  };
}
